import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { collaborationApi, usersApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Plus, Trash2, Settings2 } from 'lucide-react'
import { toast } from 'sonner'

interface TranslatorAssignmentProps {
  projectId: string
  sourceLanguage: string
  targetLanguage: string
  /** 只读模式：仅展示分配信息，隐藏操作按钮与浮层（译者视图） */
  readOnly?: boolean
}

/**
 * 表单中的一行分配记录
 * - original 存在表示是预填的已有分配（只能改范围或删除，不能改译者）
 * - original 不存在表示用户新增的行（需要选译者）
 */
interface AssignmentRow {
  key: string
  translatorId: string
  startSequence: number
  endSequence: number
  original?: {
    translatorId: string
    startSequence: number
    endSequence: number
  }
}

export function TranslatorAssignment({ projectId, sourceLanguage, targetLanguage, readOnly = false }: TranslatorAssignmentProps) {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)

  // readOnly（译者视图）：用 my-segments 端点（允许译者访问），不调 founder/pm 专属接口
  // 否则后端 requireRole(['founder','pm']) 直接 403，浏览器控制台一堆红色报错
  const { data: myAssignmentData, isLoading: myLoading } = useQuery({
    queryKey: ['project-my-segments', projectId],
    queryFn: () => collaborationApi.getMySegments(projectId),
    enabled: readOnly,
  })

  const { data: assignmentData, isLoading } = useQuery({
    queryKey: ['project-translators', projectId],
    queryFn: () => collaborationApi.getProjectTranslators(projectId),
    enabled: !readOnly,
  })

  const { data: statsData } = useQuery({
    queryKey: ['project-translator-stats', projectId],
    queryFn: () => collaborationApi.getTranslatorStats(projectId),
    enabled: !readOnly,
  })

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getList(),
    enabled: !readOnly,
  })

  const allTranslators = usersData?.data?.filter((u: any) => u.role === 'translator') || []
  // 按项目语言对筛选译者（languagePairs 形如 ["en-zh", "zh-en"]）
  const projectLangPair = `${sourceLanguage}-${targetLanguage}`
  const matchedTranslators = allTranslators.filter(
    (t: any) => Array.isArray(t.languagePairs) && t.languagePairs.includes(projectLangPair)
  )
  // 若无人配置该语言对，回退显示全部译者并提示（避免阻塞分配流程）
  const translators = matchedTranslators.length > 0 ? matchedTranslators : allTranslators
  const noLangPairMatch = matchedTranslators.length === 0 && allTranslators.length > 0
  const assignments = assignmentData?.data?.translators || []
  const segments = assignmentData?.data?.segments || []
  const stats = statsData?.data

  // —— 筛后连续编号映射（与详情页口径一致）——
  // segments 已由后端按 isLocked 过滤成仅台词段并按 sequenceNumber 升序返回，
  // 其数组下标即「密排编号」（0-based，展示时 +1，即 1..N）。
  // 弹窗内所有段落范围一律用密排编号展示与运算，提交存库时才映射回原始 sequenceNumber。
  const denseBySeq = new Map<number, number>()
  const seqByDense = new Map<number, number>()
  segments.forEach((s, i) => {
    denseBySeq.set(s.sequenceNumber, i)
    seqByDense.set(i, s.sequenceNumber)
  })
  // 原始 sequenceNumber → 密排编号（0-based）；不在可见段则原样返回（兜底）
  const toDense = (origSeq: number): number => denseBySeq.get(origSeq) ?? origSeq
  // 密排编号（0-based）→ 原始 sequenceNumber；映射不到则原样返回（兜底）
  const toOrig = (denseIdx: number): number => seqByDense.get(denseIdx) ?? denseIdx

  /**
   * 批量提交分配变更：依次调用 add/update/remove 接口
   * 任一失败则中断并提示，已成功的会通过 invalidate 刷新
   */
  const batchMutation = useMutation({
    mutationFn: async (rows: AssignmentRow[]) => {
      // 差异比较：后端返回的已有分配是「原始 sequenceNumber」，表单行是「密排编号」，
      // 统一转成密排编号后再比较，避免因编号体系不同误判为全部变更。
      const originalById = new Map(
        assignments.map(a => [
          a.translatorId,
          {
            translatorId: a.translatorId,
            // 原始编号 → 密排编号；null 边界（无范围）保持语义
            startSequence: a.startSequence != null ? toDense(a.startSequence) : null,
            endSequence: a.endSequence != null ? toDense(a.endSequence) : null,
          } as const,
        ])
      )
      const rowsById = new Map(
        rows.filter(r => r.translatorId).map(r => [r.translatorId, r] as const)
      )

      // 1. 已有但被删除的 → DELETE
      const toDelete = [...originalById.keys()].filter(id => !rowsById.has(id))
      // 2. 已有且仍存在的 → 比较范围（密排），变了 → PUT（存原始编号）
      const toUpdate: Array<{ translatorId: string; startSequence: number; endSequence: number }> = []
      for (const row of rows) {
        if (!row.translatorId) continue
        const orig = originalById.get(row.translatorId)
        if (!orig) continue // 新增的，下面处理
        if (orig.startSequence !== row.startSequence || orig.endSequence !== row.endSequence) {
          toUpdate.push({
            translatorId: row.translatorId,
            startSequence: toOrig(row.startSequence),
            endSequence: toOrig(row.endSequence),
          })
        }
      }
      // 3. 新增的 → POST（存原始编号）
      const toAdd: Array<{ translatorId: string; startSequence: number; endSequence: number }> = []
      for (const row of rows) {
        if (!row.translatorId) continue
        if (!originalById.has(row.translatorId)) {
          toAdd.push({
            translatorId: row.translatorId,
            startSequence: toOrig(row.startSequence),
            endSequence: toOrig(row.endSequence),
          })
        }
      }

      // 顺序执行（避免并发冲突同一项目段落分配）
      for (const id of toDelete) {
        await collaborationApi.removeTranslator(projectId, id)
      }
      for (const u of toUpdate) {
        await collaborationApi.updateTranslatorRange(projectId, u.translatorId, {
          startSequence: u.startSequence,
          endSequence: u.endSequence,
        })
      }
      for (const a of toAdd) {
        await collaborationApi.addTranslator(projectId, a)
      }
      return { added: toAdd.length, updated: toUpdate.length, deleted: toDelete.length }
    },
    onSuccess: (result) => {
      const parts: string[] = []
      if (result.added) parts.push(`新增 ${result.added} 位`)
      if (result.updated) parts.push(`更新 ${result.updated} 位`)
      if (result.deleted) parts.push(`移除 ${result.deleted} 位`)
      toast.success(parts.length ? `分配已保存（${parts.join('，')}）` : '分配无变更')
      queryClient.invalidateQueries({ queryKey: ['project-translators', projectId] })
      queryClient.invalidateQueries({ queryKey: ['project-translator-stats', projectId] })
      setDialogOpen(false)
    },
    onError: (error: any) => {
      toast.error(error.error || '保存失败')
    },
  })

  if (readOnly ? myLoading : isLoading) {
    return <div>加载中...</div>
  }

  // maxSequence：密排编号上限（0-based = 可见段数 - 1），弹窗展示即为 1..774
  const maxSequence = segments.length > 0 ? segments.length - 1 : 0
  const allAssigned = stats ? stats.unassigned.segmentCount === 0 : false
  // 没有可选译者且没有现有分配可调整时禁用按钮
  const noTranslatorsAvailable = translators.length === 0
  const buttonDisabled = noTranslatorsAvailable && assignments.length === 0

  // 只读模式（译者视图）：仅展示自己的分配范围，不调 founder/pm 专属接口
  // myAssignmentData 来自 GET /collaboration/projects/:id/my-segments，译者有权访问
  if (readOnly) {
    const myAssignment = myAssignmentData?.data?.assignment
    const mySegmentCount = myAssignmentData?.data?.mySegmentCount ?? 0
    const totalSegments = myAssignmentData?.data?.totalSegments ?? 0
    // 译者视角：用 my-segments 返回的可见段（仅台词段）构建密排映射，展示与详情页一致
    const roToDense = (() => {
      const visible = myAssignmentData?.data?.allSegments || []
      const idxBySeq = new Map<number, number>()
      visible.forEach((s: any, i: number) => idxBySeq.set(s.sequenceNumber, i))
      return (orig: number) => idxBySeq.get(orig) ?? orig
    })()
    const hasAssignment = myAssignment && (myAssignment.startSequence !== null || myAssignment.endSequence !== null)
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">译者分配</h3>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted-foreground">总段落 <strong className="text-foreground">{totalSegments}</strong></span>
            {hasAssignment ? (
              <span className="text-muted-foreground">
                我的范围：
                <strong className="text-foreground">
                  第 {roToDense(myAssignment!.startSequence ?? 0) + 1} - {roToDense(myAssignment!.endSequence ?? 0) + 1} 段
                </strong>
                <span className="text-muted-foreground">（共 {mySegmentCount} 段）</span>
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">暂未分配段落</span>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">译者分配</h3>
        <div className="flex items-center gap-4">
          {stats && (
            <div className="flex items-center gap-3 text-sm">
              <span className="text-muted-foreground">总段落 <strong className="text-foreground">{stats.totalSegments}</strong></span>
              <span className="text-muted-foreground">总字数 <strong className="text-foreground">{stats.totalWordCount}</strong></span>
              {stats.translators && stats.translators.length > 0 ? (
                <span className="text-muted-foreground">
                  已分配：
                  {stats.translators.map((t, i) => (
                    <span key={t.translatorId} className="text-foreground">
                      {i > 0 && <span className="text-muted-foreground">，</span>}
                      {t.translatorName}（{toDense(t.startSequence ?? 0) + 1}-{toDense(t.endSequence ?? 0) + 1}）
                    </span>
                  ))}
                </span>
              ) : (
                <span className="text-muted-foreground">已分配 <strong className="text-foreground">0</strong></span>
              )}
              {stats.unassigned.segmentCount > 0 && (
                <span className="text-muted-foreground">未分配 <strong className="text-warning">{stats.unassigned.segmentCount}</strong></span>
              )}
            </div>
          )}
          {assignments.length === 0 && (
            <span className="text-sm text-muted-foreground">暂无译者分配</span>
          )}
          <Button
            size="sm"
            onClick={() => setDialogOpen(true)}
            disabled={buttonDisabled}
            title={buttonDisabled ? '没有可选译者' : undefined}
          >
            {allAssigned ? (
              <>
                <Settings2 className="w-3.5 h-3.5 mr-1.5" />
                调整
              </>
            ) : (
              <>
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                分配译者
              </>
            )}
          </Button>
        </div>
      </div>

      <AssignmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        translators={translators}
        segments={segments}
        existingAssignments={assignments}
        maxSequence={maxSequence}
        origToDense={toDense}
        projectLangPair={projectLangPair}
        noLangPairMatch={noLangPairMatch}
        allAssigned={allAssigned}
        onSubmit={(rows) => batchMutation.mutate(rows)}
        isLoading={batchMutation.isPending}
      />
    </div>
  )
}

/**
 * 分配译者浮层（多行表单）
 *
 * - 打开时预填已分配的译者行（译者锁定不可改，只能改范围或删除）
 * - 可添加新行选择未分配的译者
 * - 每行实时显示该段落范围的字数总和
 * - 段落范围统一用「密排编号」（1..可见段数N），与详情页总段落数一致；
 *   提交时由外层 batchMutation 映射回原始 sequenceNumber 存库
 * - 提交时差异比较 → 调用 POST/PUT/DELETE
 */
function AssignmentDialog({
  open,
  onOpenChange,
  translators,
  segments,
  existingAssignments,
  maxSequence,
  origToDense,
  projectLangPair,
  noLangPairMatch,
  allAssigned,
  onSubmit,
  isLoading,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  translators: any[]
  segments: Array<{ id: string; sequenceNumber: number; sourceText: string; assignedTranslatorId?: string | null; wordCount: number }>
  existingAssignments: Array<{ translatorId: string; translatorName?: string; startSequence: number | null; endSequence: number | null }>
  maxSequence: number
  /** 原始 sequenceNumber → 密排编号（0-based），用于预填行把存库的原始编号转成展示编号 */
  origToDense: (origSeq: number) => number
  projectLangPair: string
  noLangPairMatch: boolean
  allAssigned: boolean
  onSubmit: (rows: AssignmentRow[]) => void
  isLoading: boolean
}) {
  const displayMax = maxSequence + 1

  /**
   * 把后端返回的已有分配（原始 sequenceNumber）转成表单行（密排编号，带 original 标记）
   */
  function initRows(assignments: Array<{ translatorId: string; startSequence: number | null; endSequence: number | null }>): AssignmentRow[] {
    return assignments.map(a => ({
      key: `orig-${a.translatorId}`,
      translatorId: a.translatorId,
      startSequence: a.startSequence != null ? origToDense(a.startSequence) : 0,
      endSequence: a.endSequence != null ? origToDense(a.endSequence) : 0,
      original: {
        translatorId: a.translatorId,
        startSequence: a.startSequence != null ? origToDense(a.startSequence) : 0,
        endSequence: a.endSequence != null ? origToDense(a.endSequence) : 0,
      },
    }))
  }

  // 初始化表单：预填已分配的译者行
  const [rows, setRows] = useState<AssignmentRow[]>(() => initRows(existingAssignments))

  // open 从 false→true 时重新初始化（保证打开浮层时数据是最新的）
  // React 推荐的 "adjust state when prop changes" 模式，会丢弃当前 render 输出用新 state 重渲染
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setRows(initRows(existingAssignments))
    }
  }

  // 已被选中的译者 ID 集合（用于过滤新增行的可选译者）
  const selectedIds = new Set(rows.filter(r => r.translatorId).map(r => r.translatorId))

  /**
   * 计算指定段落范围（0-based 密排编号）的字数总和
   * 直接对后端返回的每段 wordCount 求和：该值由后端按与项目头部
   * sourceWordCount 完全一致的口径（短剧剥离人物名/括号提示/角色名后计）算好，
   * 前端不再各自用 countWords 全量统计，保证浮层字数与详情页一致。
   * segments 已按 sequenceNumber 升序且仅台词段，数组下标即密排编号。
   */
  function calcWordCount(startSeq: number, endSeq: number): number {
    if (startSeq > endSeq) return 0
    return segments
      .filter((_, idx) => idx >= startSeq && idx <= endSeq)
      .reduce((sum, s) => sum + (s.wordCount ?? 0), 0)
  }

  /**
   * 找出第一个连续未分配的段落区间（0-based 密排编号）
   *
   * 算法：
   *   1. 收集所有已选译者的有效行占用的段落号（密排）
   *   2. 从 0 扫到 maxSequence，跳过已占用段落
   *   3. 找到第一个未占用的段落作为 start，继续扫描到下一个已占用或末尾作为 end
   *
   * 用于「添加译者」时给新行一个合理的默认范围，避免直接给 0-maxSequence
   * 导致用户每次都要手动改。
   *
   * @returns { start, end } 或 null（所有段落都已占用）
   */
  function findFirstUnassignedRange(): { start: number; end: number } | null {
    const occupied = new Set<number>()
    for (const r of rows) {
      if (!r.translatorId) continue // 未选译者的行不算占用
      if (r.startSequence > r.endSequence) continue
      for (let i = r.startSequence; i <= r.endSequence; i++) {
        occupied.add(i)
      }
    }
    for (let i = 0; i <= maxSequence; i++) {
      if (!occupied.has(i)) {
        // 找到第一个未占用，继续找连续未占用的末尾
        let end = i
        while (end + 1 <= maxSequence && !occupied.has(end + 1)) {
          end++
        }
        return { start: i, end }
      }
    }
    return null
  }

  /**
   * 检测所有已选译者行之间的段落范围冲突
   *
   * 两个区间 [a1,b1] 与 [a2,b2] 相交的定义：a1 <= b2 && a2 <= b1
   * 返回所有有冲突的行 key（至少出现一次冲突的行），用于 UI 高亮和提交拦截
   */
  function findConflictRowKeys(): Set<string> {
    const filled = rows.filter(r => r.translatorId && r.startSequence <= r.endSequence)
    const conflictKeys = new Set<string>()
    for (let i = 0; i < filled.length; i++) {
      for (let j = i + 1; j < filled.length; j++) {
        const a = filled[i]
        const b = filled[j]
        // 区间相交：a.start <= b.end && b.start <= a.end
        if (a.startSequence <= b.endSequence && b.startSequence <= a.endSequence) {
          conflictKeys.add(a.key)
          conflictKeys.add(b.key)
        }
      }
    }
    return conflictKeys
  }

  function addRow() {
    // 新增译者默认范围 = 第一个连续未分配段落区间；若全部分配完则给 0-maxSequence（用户自行调整）
    const range = findFirstUnassignedRange()
    setRows(prev => [
      ...prev,
      {
        key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        translatorId: '',
        startSequence: range?.start ?? 0,
        endSequence: range?.end ?? maxSequence,
      },
    ])
  }

  function updateRow(key: string, patch: Partial<AssignmentRow>) {
    setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)))
  }

  function removeRow(key: string) {
    setRows(prev => prev.filter(r => r.key !== key))
  }

  // 实时计算冲突行 key 集合，用于 UI 高亮 + 提交拦截
  const conflictKeys = findConflictRowKeys()
  const hasConflict = conflictKeys.size > 0

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    // 校验
    const filled = rows.filter(r => r.translatorId)
    if (filled.length === 0) {
      toast.error('请至少添加一位译者')
      return
    }
    for (const r of filled) {
      if (r.startSequence > r.endSequence) {
        toast.error('起始段落不能大于结束段落')
        return
      }
    }
    if (hasConflict) {
      toast.error('存在段落范围冲突，请先调整后再保存')
      return
    }
    onSubmit(rows)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{allAssigned ? '调整译者分配' : '分配译者'}</DialogTitle>
            <DialogDescription>
              {allAssigned
                ? `调整已有译者的段落范围（当前语言对：${projectLangPair}）`
                : `为项目分配译者并设置段落范围（当前语言对：${projectLangPair}）`}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-4 max-h-[60vh] overflow-y-auto">
            {noLangPairMatch && (
              <p className="text-sm text-warning bg-warning/10 border border-warning/30 rounded-md p-2">
                暂无译者配置了「{projectLangPair}」语言对，已显示全部译者。可在用户管理中为译者配置语言对。
              </p>
            )}
            {rows.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                暂无分配，点击下方「添加译者」开始
              </p>
            )}
            {rows.map((row) => {
              const translator = translators.find(t => t.id === row.translatorId)
              const isOriginal = !!row.original
              // 新增行的可选译者 = 全部译者 - 已被其他行选中的
              const availableForNew = translators.filter(t => !selectedIds.has(t.id) || t.id === row.translatorId)
              const wordCount = calcWordCount(row.startSequence, row.endSequence)
              const segmentCount = row.endSequence - row.startSequence + 1
              const isConflict = conflictKeys.has(row.key)
              const rangeInvalid = row.startSequence > row.endSequence
              return (
                <div
                  key={row.key}
                  className={`grid grid-cols-12 gap-2 items-end border-b pb-3 ${isConflict ? 'bg-destructive/10 -mx-2 px-2 rounded' : ''}`}
                >
                  <div className="col-span-12 sm:col-span-4 space-y-1">
                    <Label className="text-xs">译者</Label>
                    {isOriginal ? (
                      // 已有分配的译者不可改，只能改范围或删除
                      <div className="h-9 flex items-center px-3 rounded-md border bg-muted text-sm">
                        {translator?.name || row.translatorId}
                      </div>
                    ) : (
                      <Select
                        value={row.translatorId}
                        onValueChange={(value) => updateRow(row.key, { translatorId: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="选择译者" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableForNew.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name} ({t.email}){t.languagePairs?.length > 0 ? ` · ${t.languagePairs.join(', ')}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="col-span-4 sm:col-span-2 space-y-1">
                    <Label className="text-xs">起始段</Label>
                    <Input
                      type="number"
                      min={1}
                      max={displayMax}
                      value={row.startSequence + 1}
                      onChange={(e) => updateRow(row.key, { startSequence: Math.max(0, Math.min(maxSequence, (parseInt(e.target.value) || 1) - 1)) })}
                      className={isConflict ? 'border-destructive/50' : ''}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2 space-y-1">
                    <Label className="text-xs">结束段</Label>
                    <Input
                      type="number"
                      min={1}
                      max={displayMax}
                      value={row.endSequence + 1}
                      onChange={(e) => updateRow(row.key, { endSequence: Math.max(0, Math.min(maxSequence, (parseInt(e.target.value) || 1) - 1)) })}
                      className={isConflict ? 'border-destructive/50' : ''}
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-3 space-y-1">
                    <Label className="text-xs">{isConflict ? '⚠️ 冲突' : rangeInvalid ? '⚠️ 范围' : '字数'}</Label>
                    <div className={`h-9 flex items-center px-3 rounded-md border text-sm ${
                      isConflict
                        ? 'border-destructive/50 bg-destructive/10 text-destructive'
                        : rangeInvalid
                          ? 'border-destructive/50 bg-destructive/10 text-destructive'
                          : 'bg-muted/50 text-muted-foreground'
                    }`}>
                      {rangeInvalid
                        ? '起 > 止'
                        : `${segmentCount} 段 · ${wordCount} 字`}
                    </div>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-9 px-2"
                      onClick={() => removeRow(row.key)}
                      title="移除该行"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              )
            })}
            {/* 添加新行按钮：仅当还有未选译者时显示 */}
            {translators.filter(t => !selectedIds.has(t.id)).length > 0 && (
              <Button type="button" variant="outline" size="sm" onClick={addRow} className="w-full">
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                添加译者
              </Button>
            )}
            <p className="text-sm text-muted-foreground">
              段落范围：1 - {displayMax}（共 {displayMax} 个段落）
            </p>
          </div>
          <DialogFooter>
            {hasConflict && (
              <p className="text-xs text-destructive mr-auto">
                ⚠️ 存在段落范围冲突，请调整后再保存
              </p>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
            <Button type="submit" disabled={isLoading || hasConflict}>
              {isLoading ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
